import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { isCustomerRole } from '@itr/shared';
import { ThemeProvider, useTheme } from './theme/ThemeProvider';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { RealtimeProvider } from './api/realtime';
import { ToastProvider } from './components/ui';
import { Shell } from './components/Shell';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { ResourceDetail, ResourcesPage } from './pages/Resources';
import { ViciDialMonitor } from './pages/ViciDialMonitor';
import { GoipMonitor } from './pages/GoipMonitor';
import { ApplianceLines } from './pages/ApplianceLines';
import { LoadBalancerMonitor } from './pages/LoadBalancerMonitor';
import { InstallBalancer } from './pages/InstallBalancer';
import { MagnusMonitor } from './pages/MagnusMonitor';
import { AlertDetail, AlertsPage } from './pages/Alerts';
import { AlertRulesPage } from './pages/AlertRules';
import { MonitoringPage } from './pages/Monitoring';
import { SecurityPage } from './pages/Security';
import { ServiceDesk, TicketDetail } from './pages/ServiceDesk';
import { TaskDetail, TasksPage } from './pages/Tasks';
import { VaultDetail, VaultPage } from './pages/Vault';
import { ToolsPage } from './pages/Tools';
import { AccessPage } from './pages/Access';
import { BillingDocumentsPage, InvoiceDetail } from './pages/Invoices';
import { UtilityBillsPage } from './pages/UtilityBills';
import { BackupsPage } from './pages/Backups';
import { AccountDetail, BillingPage } from './pages/Billing';
import { ClientDetail, ClientsPage, ReportPage, SiteDetail } from './pages/Clients';
import { PluginsPage } from './pages/Plugins';
import { SettingsPage } from './pages/Settings';

// xterm.js is a sizeable dependency that only the rare terminal session
// needs — split out of the main bundle rather than paid for on every login.
const TerminalPage = lazy(() =>
  reloadOnStaleChunk(() => import('./pages/Terminal').then((m) => ({ default: m.TerminalPage }))),
);

/**
 * A tab opened before a deploy still references the old hashed chunk names,
 * which the new image no longer ships. Rather than crash, reload once so the
 * tab picks up the current index.html; the timestamp guard stops a genuinely
 * broken chunk from turning into a reload loop.
 */
function reloadOnStaleChunk<T>(load: () => Promise<T>): Promise<T> {
  const key = 'itr:chunk-reload';
  return load().catch((err: unknown) => {
    let last = 0;
    try {
      last = Number(sessionStorage.getItem(key) ?? 0);
      sessionStorage.setItem(key, String(Date.now()));
    } catch {
      /* storage unavailable — fall through to the time check with last = 0 */
    }
    if (Date.now() - last < 30_000) throw err;
    location.reload();
    return new Promise<T>(() => {});
  });
}

import './styles/global.css';

export function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <Root />
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

function Root() {
  const { user, loading } = useAuth();
  const { reloadThemes } = useTheme();

  // Themes are only readable once authenticated; reload them the moment a
  // session appears so a white-labelled tenant's branding applies immediately.
  useEffect(() => {
    if (user) void reloadThemes();
  }, [user, reloadThemes]);

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100dvh', background: 'var(--itr-bg)' }}>
        <span className="spinner" style={{ width: 22, height: 22 }} />
      </div>
    );
  }

  if (!user) return <Login />;

  return (
    <RealtimeProvider>
      <Routes>
        {/* Outside the Shell wrapper on purpose: a terminal wants the full
            viewport, and it always opens in its own tab from an Access card. */}
        <Route
          path="resources/:id/terminal/:service"
          element={
            <Suspense fallback={<div style={{ height: '100dvh', background: '#0b0d12' }} />}>
              <TerminalPage />
            </Suspense>
          }
        />

        <Route element={<Shell />}>
          <Route index element={<Home />} />

          <Route path="resources" element={<ResourcesPage />} />
          <Route path="resources/:id" element={<ResourceDetail />} />
          <Route path="resources/:id/monitor" element={<ViciDialMonitor />} />
          <Route path="resources/:id/lines" element={<GoipMonitor />} />
          <Route path="resources/:id/appliance-lines" element={<ApplianceLines />} />
          <Route path="resources/:id/balancer" element={<LoadBalancerMonitor />} />
          <Route path="resources/:id/balancer/install" element={<InstallBalancer />} />
          <Route path="resources/:id/magnus" element={<MagnusMonitor />} />
          {/* One route serves every per-plugin resource view; the plugin id is
              just a filter, so a new plugin needs no routing changes. */}
          <Route path="r/:pluginId" element={<PluginResources />} />

          <Route path="monitoring" element={<MonitoringPage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="alerts" element={<AlertsPage />} />
          <Route path="alerts/rules" element={<AlertRulesPage />} />
          <Route path="alerts/:id" element={<AlertDetail />} />

          <Route path="tickets" element={<ServiceDesk />} />
          <Route path="tickets/:id" element={<TicketDetail />} />
          <Route path="bugs" element={<ServiceDesk bugsOnly />} />

          <Route path="tasks" element={<TasksPage />} />
          <Route path="tasks/:id" element={<TaskDetail />} />

          <Route path="tools" element={<ToolsPage />} />
          <Route path="access" element={<AccessPage />} />
          <Route path="invoices" element={<BillingDocumentsPage />} />
          <Route path="invoices/:id" element={<InvoiceDetail />} />
          <Route path="utility-bills" element={<UtilityBillsPage />} />

          <Route path="vault" element={<VaultPage />} />
          <Route path="vault/:id" element={<VaultDetail />} />

          <Route path="backups" element={<BackupsPage />} />

          <Route path="billing" element={<BillingPage />} />
          <Route path="billing/:id" element={<AccountDetail />} />

          <Route path="clients" element={<ClientsPage />} />
          <Route path="clients/:id" element={<ClientDetail />} />
          <Route path="clients/:id/report" element={<ReportPage />} />
          <Route path="clients/:id/sites/:siteId" element={<SiteDetail />} />

          <Route path="plugins" element={<PluginsPage />} />
          <Route path="settings" element={<SettingsPage />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </RealtimeProvider>
  );
}

/**
 * A client has no use for the NOC widget wall — their whole account is one
 * tenant, and `/clients/:id` already renders exactly that scope (live health,
 * uptime history, open alerts/tickets) with every write action gated behind
 * the permissions they may or may not hold. Land them there directly instead
 * of behind the client picker meant for staff managing many tenants.
 */
function Home() {
  const { user } = useAuth();
  if (isCustomerRole(user?.role) && user?.customerId) {
    return <Navigate to={`/clients/${user.customerId}`} replace />;
  }
  return <Dashboard />;
}

function PluginResources() {
  const { pluginId } = useParams<{ pluginId: string }>();
  // Remount on plugin change so filters and search reset between sections.
  return <ResourcesPage key={pluginId} pluginFilter={pluginId} />;
}
